import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useApiData } from '@/composables/useApiData'
import { useFetch } from '@/composables/useFetch'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'
import useHomeConfiguration from '@/composables/useHomeConfiguration'
import { selectCoverPhoto } from '@/classes/CoverPhotoSelector'
import { randomNumber } from '@/helpers/helpers'

// Legacy parity: testimonials were cached server-side for 24h
// (HttpContext.Cache), and the cover-photo sheet changes rarely. Both raw
// lists are cached in localStorage for 24h; the random picks still happen on
// every page load.
const CACHE_TTL = 24 * 60 * 60 * 1000

const readCache = (key) => {
  try {
    const cached = JSON.parse(window.localStorage.getItem(key))
    if (!cached?.data) return null
    return { data: cached.data, isStale: Date.now() - cached.timestamp > CACHE_TTL }
  } catch {
    return null
  }
}

const writeCache = (key, data) => {
  window.localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }))
}

export let useHomeStore = defineStore('homeStore', () => {
  const {
    $COVER_PHOTO_BRAND_KEY,
    $DEFAULT_COVER,
    $REQUESTED_COVER_PHOTO,
    $REQUESTED_FEATURE_ID,
    $TESTIMONIALS
  } = useHomeConfiguration()

  const { $BASEURL } = useConfigurationGlobals()
  const { request } = useApiData('common')

  const coverPhoto = ref($DEFAULT_COVER)
  const testimonial = ref(null)
  // Legacy getSelectedFeature(): random 1-3 per page load (dev can pin via ?feature=)
  const featureId = ref($REQUESTED_FEATURE_ID || randomNumber(1, 3))

  const fetchCoverPhotoSheet = async () => {
    // Deliberately NOT useApiData: getCoverPhotos rejects tokens (401 for any
    // wf_token, anonymous or site) but serves the sheet on a bare request. It
    // also returns the raw Sheets shape with no status envelope.
    const { data, error } = await useFetch(
      `${$BASEURL}/common/getCoverPhotos?brand=${$COVER_PHOTO_BRAND_KEY}`
    )

    const values = data.value?.values
    if (!values && error.value) throw error.value

    return values ?? null
  }

  const select = (sheetValues) =>
    selectCoverPhoto({
      sheetValues,
      brandKey: $COVER_PHOTO_BRAND_KEY,
      requestedBrand: $REQUESTED_COVER_PHOTO,
      isProduction: import.meta.env.PROD
    })

  const loadCoverPhoto = async () => {
    // Brands without a sheet (everything but iKitesurf/iWindsurf) keep the
    // config default — matching legacy GetBrandDefaults().
    if (!$COVER_PHOTO_BRAND_KEY) return

    const cacheKey = `HomeCoverPhotos-${$COVER_PHOTO_BRAND_KEY}`

    try {
      const cached = readCache(cacheKey)

      if (cached) {
        // Select from the cached sheet immediately (no fetch before first
        // paint); refresh a stale list in the background for the next load.
        coverPhoto.value = select(cached.data) || $DEFAULT_COVER

        if (cached.isStale) {
          fetchCoverPhotoSheet()
            .then((values) => values && writeCache(cacheKey, values))
            .catch(() => {})
        }
        return
      }

      const values = await fetchCoverPhotoSheet()
      if (!values) return

      writeCache(cacheKey, values)
      coverPhoto.value = select(values) || $DEFAULT_COVER
    } catch (error) {
      // Legacy behavior: any fetch/parse failure keeps the brand default.
      console.warn('Cover photo selection failed, using brand default', error)
    }
  }

  const loadTestimonial = async () => {
    // Config-injected testimonials take priority: the dev config supplies
    // fixtures, and a host page could inject a server-fetched list. Used
    // because common/getTestimonials currently 401s for site tokens
    // (server-to-server only) — the API fetch below starts working the day
    // that endpoint is authorized for lifestyle tokens.
    if ($TESTIMONIALS?.length) {
      testimonial.value = $TESTIMONIALS[Math.floor(Math.random() * $TESTIMONIALS.length)]
      return
    }

    const cacheKey = 'HomeTestimonials'

    try {
      let cached = readCache(cacheKey)

      if (!cached || cached.isStale) {
        const { data, error } = await request('getTestimonials')
        if (error.value) throw error.value

        const testimonials = data.value?.testimonials
        if (!testimonials?.length) return

        writeCache(cacheKey, testimonials)
        cached = { data: testimonials }
      }

      // Random pick per page load, from the cached list — matching the
      // legacy per-request objRand.Next selection.
      testimonial.value = cached.data[Math.floor(Math.random() * cached.data.length)]
    } catch (error) {
      // Legacy behavior: the testimonial block simply doesn't render.
      console.warn('Testimonials unavailable', error)
    }
  }

  return {
    coverPhoto,
    testimonial,
    featureId,
    loadCoverPhoto,
    loadTestimonial
  }
})
